/**
 * Text formatting utilities
 */

/**
 * Decodes HTML entities in a string
 * @param text - The text with HTML entities to decode
 * @returns Decoded string
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text) return text;
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Converts basic markdown formatting to HTML
 * @param text - The markdown text to convert
 * @returns HTML formatted string
 */
export const convertMarkdownToHtml = (text: string): string => {
  if (!text) return text;
  
  // First decode any HTML entities that might be in the text
  let html = decodeHtmlEntities(text);
  
  // Convert headers first (### Header -> <h3>Header</h3>)
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  
  // Convert markdown links ([text](url) -> <a href="url" target="_blank">text</a>)
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Convert bold text first (**text** -> <strong>text</strong>)
  // Simple non-greedy match that works with any content between **
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert italic text (*text* -> <em>text</em>) after bold
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Auto-link bare URLs (https://... or http://...) that are not already inside an <a> tag.
  // Runs after bold/italic so stray * characters adjacent to URLs are already consumed.
  // The character class excludes whitespace, common markdown punctuation, and HTML delimiters.
  html = html.replace(/(<a[\s\S]*?<\/a>|<[^>]+>)|((https?:\/\/)[^\s<>"')\]*_]+)/g, (match, htmlTag, url) => {
    if (htmlTag) return htmlTag; // preserve existing HTML tags/anchors as-is
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
  
  // Convert unordered lists (- item or * item) - process before line breaks
  html = html.replace(/^[-*] (.+)$/gm, '<li>$1</li>');
  
  // Convert ordered lists (1. item, 2. item, etc.) - process before line breaks
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  
  // Wrap consecutive list items in <ul> or <ol> tags
  // For unordered lists (created from - or *)
  html = html.replace(/(<li>.*<\/li>)(?:\s*<li>.*<\/li>)*/gs, (match) => {
    return '<ul>' + match + '</ul>';
  });
  
  // Convert all line breaks to <br> tags
  // Keep double <br><br> as two line breaks (don't convert to paragraph breaks)
  html = html.replace(/\r?\n/g, '<br>');
  
  // Clean up line breaks inside lists
  html = html.replace(/<li>([^<]*)<br>/g, '<li>$1');
  html = html.replace(/<\/li><br>/g, '</li>');
  
  // Wrap the content in paragraph tags if it doesn't start with a header or list
  if (!html.startsWith('<h') && !html.startsWith('<ul>') && !html.startsWith('<ol>')) {
    html = '<p>' + html + '</p>';
  }
  
  // Clean up any empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p><br><\/p>/g, '');
  
  return html;
};

/**
 * Converts plain text newlines to HTML line breaks
 * @param text - The plain text to convert
 * @returns HTML formatted string with <br> tags
 */
export const convertNewlinesToHtml = (text: string): string => {
  if (!text) return text;
  // Replace \n with <br> and ensure proper HTML formatting
  return text.replace(/\n/g, '<br>');
};

/**
 * Converts HTML to plain text while preserving newlines from block elements and <br> tags.
 * Use this instead of stripHtmlTags when whitespace structure matters (e.g. chat textarea).
 */
export const htmlToPlainText = (html: string): string => {
  if (!html) return html;
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Strips HTML tags from a string, leaving only the text content
 * @param html - The HTML string to strip tags from
 * @returns Plain text string without HTML tags
 * 
 * @example
 * stripHtmlTags('<em class="highlight">Parse</em> POS Homepage')
 * // Returns: 'Parse POS Homepage'
 * 
 * stripHtmlTags('Plain text')
 * // Returns: 'Plain text' (unchanged)
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return html;
  
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Extract text content (this automatically strips all HTML tags)
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Decodes MIME encoded-word strings according to RFC 2047
 * Handles email headers that contain non-ASCII characters
 * Format: =?charset?encoding?encoded-text?=
 * 
 * Supports:
 * - Q-encoding (Quoted-Printable): =?UTF-8?Q?text?=
 * - B-encoding (Base64): =?UTF-8?B?text?=
 * 
 * @param text - The text that may contain MIME encoded-words
 * @returns Decoded string, or original string if no encoding detected
 * 
 * @example
 * decodeMimeEncodedWord('=?UTF-8?Q?Victoria_Brosch=C3=A9?=')
 * // Returns: 'Victoria Brosché'
 * 
 * decodeMimeEncodedWord('Victoria Brosché')
 * // Returns: 'Victoria Brosché' (unchanged)
 */
export const decodeMimeEncodedWord = (text: string): string => {
  if (!text) return text;
  
  // RFC 2047 MIME encoded-word pattern: =?charset?encoding?encoded-text?=
  const mimePattern = /=\?([^?]+)\?([QB])\?([^?]*)\?=/gi;
  
  // Check if text contains any MIME encoded-words
  if (!mimePattern.test(text)) {
    // No MIME encoding detected - return as-is
    return text;
  }
  
  // Reset regex lastIndex after test()
  mimePattern.lastIndex = 0;
  
  try {
    // Decode all MIME encoded-words in the text
    return text.replace(mimePattern, (match, charset, encoding, encodedText) => {
      try {
        const upperEncoding = encoding.toUpperCase();
        
        if (upperEncoding === 'Q') {
          // Q-encoding (Quoted-Printable variant for headers)
          // 1. Replace underscores with spaces
          let decoded = encodedText.replace(/_/g, ' ');
          
          // 2. Decode =XX hex sequences to bytes and handle UTF-8 properly
          // Collect all bytes first
          const bytes: number[] = [];
          let i = 0;
          while (i < decoded.length) {
            if (decoded[i] === '=' && i + 2 < decoded.length) {
              // Hex-encoded byte
              const hex = decoded.substring(i + 1, i + 3);
              bytes.push(parseInt(hex, 16));
              i += 3;
            } else {
              // Regular ASCII character
              bytes.push(decoded.charCodeAt(i));
              i++;
            }
          }
          
          // Convert bytes to UTF-8 string using TextDecoder
          const uint8Array = new Uint8Array(bytes);
          const decoder = new TextDecoder(charset.toLowerCase());
          return decoder.decode(uint8Array);
        } else if (upperEncoding === 'B') {
          // B-encoding (Base64)
          try {
            // Use built-in atob for Base64 decoding
            const decodedBytes = atob(encodedText);
            
            // Convert bytes to UTF-8 string
            // Handle UTF-8 multi-byte sequences properly
            const bytes = new Uint8Array(decodedBytes.length);
            for (let i = 0; i < decodedBytes.length; i++) {
              bytes[i] = decodedBytes.charCodeAt(i);
            }
            
            // Use TextDecoder for proper UTF-8 decoding
            const decoder = new TextDecoder(charset.toLowerCase());
            return decoder.decode(bytes);
          } catch (base64Error) {
            console.warn('Failed to decode Base64 MIME encoded-word:', base64Error);
            return match; // Return original if decoding fails
          }
        }
        
        // Unsupported encoding - return original
        return match;
      } catch (error) {
        console.warn('Failed to decode MIME encoded-word:', error);
        return match; // Return original if decoding fails
      }
    });
  } catch (error) {
    console.warn('Failed to process MIME encoded text:', error);
    return text; // Return original text if processing fails
  }
}; 